import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import {
  Trash2,
  Plus,
  Shield,
  Check,
  X,
  Lock,
  Edit
} from "lucide-react";
import { showToast } from "../utils/toastUtils";

const availablePages = [
  "Dashboard",
  "Summary",
  "Profile",
  "Notifications",
  "Products",
  "Create Order",
  "All Orders",
  "New Orders",
  "Pending Packed",
  "Add Courier",
  "Claim Payment",
  "Payment History",
  "Add Roles",
  "Add Users",
  "Pending Confirm",
  "Order Heatmap",
  "Store Insights",
  "Profit & Expense",
];

const BASE_URL = process.env.REACT_APP_API_URL;

function AddRoles() {
  const { token } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [selectedPages, setSelectedPages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  useEffect(() => {
    if (token) axios.defaults.headers.common["x-auth-token"] = token;
    fetchRoles();
  }, [token]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/roles`)
      setRoles(res.data);
    } catch (err) {
      showToast("Error", "Failed to load roles", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageToggle = (page) => {
    setSelectedPages((prev) =>
      prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page]
    );
  };

  const handleSaveRole = async () => {
    if (!newRoleName || selectedPages.length === 0) {
      showToast("Validation Error", "Please enter role name and select at least one page", "error");
      return;
    }

    try {
      if (editingRole) {
        // Update existing role
        const res = await axios.put(`${BASE_URL}/api/roles/${editingRole._id}`, {
          roleName: newRoleName,
          accessiblePages: selectedPages,
        });
        setRoles(roles.map(r => r._id === editingRole._id ? res.data : r));
        showToast("Success", "Role updated successfully", "success");
      } else {
        // Create new role
        const res = await axios.post(`${BASE_URL}/api/roles`, {
          roleName: newRoleName,
          accessiblePages: selectedPages,
        });
        setRoles([...roles, res.data]);
        showToast("Success", "Role added successfully", "success");
      }
      closeModal();
    } catch (err) {
      showToast("Error", editingRole ? "Failed to update role" : "Failed to add role", "error");
    }
  };

  const confirmDeleteRole = (role) => {
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    try {
      await axios.delete(`${BASE_URL}/api/roles/${roleToDelete._id}`);
      setRoles(roles.filter((r) => r._id !== roleToDelete._id));
      showToast("Success", "Role deleted successfully", "success");
    } catch (err) {
      showToast("Error", "Failed to delete role", "error");
    } finally {
      setShowDeleteModal(false);
      setRoleToDelete(null);
    }
  };

  const openModal = (roleToEdit = null) => {
    if (roleToEdit) {
      setEditingRole(roleToEdit);
      setNewRoleName(roleToEdit.roleName);
      setSelectedPages(roleToEdit.accessiblePages || []);
    } else {
      setEditingRole(null);
      setNewRoleName("");
      setSelectedPages([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    setNewRoleName("");
    setSelectedPages([]);
  }

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" /> User Roles & Permissions
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Define roles to control access to different parts of the application.
            </p>
          </div>
          {/* Add Button (Mobile) */}
          <button
            onClick={() => openModal()}
            className="md:hidden flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all"
          >
            <Plus className="w-5 h-5" /> Add New Role
          </button>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

          {/* Create New Role Card */}
          <div
            onClick={() => openModal()}
            className="hidden md:flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all text-gray-500 hover:text-blue-600 group h-full min-h-[200px]"
          >
            <div className="h-14 w-14 bg-gray-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
              <Plus className="h-8 w-8 text-gray-400 group-hover:text-blue-600" />
            </div>
            <h3 className="text-lg font-medium">Create New Role</h3>
            <p className="text-sm text-center mt-2 px-4">Define a new set of permissions for users.</p>
          </div>

          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-64 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-full"></div>
                  <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                </div>
              </div>
            ))
          ) : (
            roles.map((role) => (
              <div key={role._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all flex flex-col h-full">
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                        <Lock className="w-5 h-5" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{role.roleName}</h3>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); openModal(role); }}
                        className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                        title="Edit Role"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); confirmDeleteRole(role); }}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                        title="Delete Role"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Accessible Pages ({role.accessiblePages.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {role.accessiblePages.slice(0, 5).map((page, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                          {page}
                        </span>
                      ))}
                      {role.accessiblePages.length > 5 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                          +{role.accessiblePages.length - 5} more
                        </span>
                      )}
                      {role.accessiblePages.length === 0 && (
                        <span className="text-sm text-gray-400 italic">No pages assigned</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">ID: <span className="font-mono">{role._id.slice(-6)}</span></span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Role Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">{editingRole ? "Edit Role" : "Create New Role"}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Role Name</label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Moderator, Analyst..."
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Accessible Pages</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availablePages.map((page) => (
                    <label
                      key={page}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${selectedPages.includes(page)
                        ? "bg-blue-50 border-blue-200 ring-1 ring-blue-500"
                        : "bg-white border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                        }`}
                    >
                      <div className={`flex-shrink-0 h-5 w-5 rounded border flex items-center justify-center mr-3 ${selectedPages.includes(page) ? "bg-blue-600 border-blue-600" : "border-gray-300 bg-white"
                        }`}>
                        {selectedPages.includes(page) && <Check className="h-3.5 w-3.5 text-white" />}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={selectedPages.includes(page)}
                        onChange={() => handlePageToggle(page)}
                      />
                      <span className={`text-sm ${selectedPages.includes(page) ? "font-medium text-blue-900" : "text-gray-700"}`}>
                        {page}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRole}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                {editingRole ? "Save Changes" : "Create Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Role?</h2>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Are you sure you want to delete the role <strong>{roleToDelete?.roleName}</strong>?
              Users assigned to this role may lose access.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 shadow-sm transition-all"
              >
                Delete Role
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default AddRoles;
